import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus } from 'lucide-react';
import type {
  AttendanceDayRow,
  BackfillSessionRequest,
  TaskSummary,
  TaskWorkOutcome,
} from '@bits/shared/zod';
import { api } from '@/lib/api';
import { declarableTasksOf, defaultOutcomeFor } from '@/lib/my-tasks';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { TaskPicker } from '@/components/TaskPicker';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Line {
  taskId: string;
  label: string;
  minutes: number;
  outcome: TaskWorkOutcome;
}

/**
 * Days a superior has allowed this person to file after the fact. The times are
 * typed rather than clocked, so the same balance rule as clock-out applies:
 * declared minutes must add up to the span worked.
 */
export function BackfillDayCard(): React.ReactElement | null {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState<AttendanceDayRow | null>(null);

  const daysQuery = useQuery({
    queryKey: ['work-log', 'backfillable'],
    queryFn: () => api.listBackfillableDays(),
  });

  const days = daysQuery.data ?? [];
  if (days.length === 0) return null;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Days you can still log</CardTitle>
          <CardDescription>
            A superior allowed you to file these after the fact. Each permission covers one day.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {days.map((d) => (
            <div
              key={d.businessDate}
              className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{d.businessDate}</div>
                {d.note ? (
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{d.note}</div>
                ) : null}
              </div>
              <Button size="sm" onClick={() => setOpen(d)}>
                <CalendarPlus className="size-4" />
                Log this day
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <BackfillDialog
        day={open}
        onClose={() => setOpen(null)}
        onDone={async () => {
          setOpen(null);
          await queryClient.invalidateQueries({ queryKey: ['work-log'] });
        }}
      />
    </>
  );
}

function BackfillDialog({
  day,
  onClose,
  onDone,
}: {
  day: AttendanceDayRow | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}): React.ReactElement {
  const [from, setFrom] = React.useState('09:30');
  const [to, setTo] = React.useState('18:30');
  const [lines, setLines] = React.useState<Line[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const tasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.getMyTasks(),
    enabled: day !== null,
    retry: false,
  });

  const openTasks: TaskSummary[] = React.useMemo(
    () => declarableTasksOf(tasksQuery.data),
    [tasksQuery.data],
  );

  React.useEffect(() => {
    if (day) {
      setLines([]);
      setError(null);
    }
  }, [day]);

  // IST wall-clock times on the granted day, sent as instants.
  const istInstant = (date: string, hhmm: string): string => {
    const [h, m] = hhmm.split(':').map(Number);
    const utcMs = Date.UTC(
      Number(date.slice(0, 4)),
      Number(date.slice(5, 7)) - 1,
      Number(date.slice(8, 10)),
      h ?? 0,
      m ?? 0,
    );
    return new Date(utcMs - (5 * 60 + 30) * 60_000).toISOString();
  };

  const workedMinutes = React.useMemo(() => {
    const [fh, fm] = from.split(':').map(Number);
    const [th, tm] = to.split(':').map(Number);
    return Math.max(0, (th ?? 0) * 60 + (tm ?? 0) - ((fh ?? 0) * 60 + (fm ?? 0)));
  }, [from, to]);

  const assigned = lines.reduce((sum, l) => sum + (Number.isFinite(l.minutes) ? l.minutes : 0), 0);
  const remaining = workedMinutes - assigned;

  const submit = useMutation({
    mutationFn: () => {
      if (!day) throw new Error('no day');
      const splits: BackfillSessionRequest['splits'] = lines
        .filter((l) => l.minutes > 0)
        .map((l) => ({
          taskId: l.taskId as string | null,
          minutes: l.minutes,
          outcome: l.outcome as TaskWorkOutcome | null,
        }));
      if (remaining > 0) splits.push({ taskId: null, minutes: remaining, outcome: null });
      return api.backfillWorkSession(day.businessDate, {
        clockInAt: istInstant(day.businessDate, from),
        clockOutAt: istInstant(day.businessDate, to),
        splits,
      });
    },
    onSuccess: onDone,
    onError: (err) => setError(toMessage(err)),
  });

  const unused = openTasks.filter((t) => !lines.some((l) => l.taskId === t.id));

  return (
    <Dialog open={day !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        {day ? (
          <>
            <DialogHeader>
              <DialogTitle>Log {day.businessDate}</DialogTitle>
              <DialogDescription>
                Enter when you started and finished, then say where the time went. Anything you
                leave unassigned stays unmapped.
              </DialogDescription>
            </DialogHeader>

            {error ? (
              <Alert variant="destructive">
                <AlertTitle>Could not save</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit.mutate();
              }}
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bf-from">Started</Label>
                  <Input
                    id="bf-from"
                    type="time"
                    value={from}
                    onChange={(e) => setFrom(e.currentTarget.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bf-to">Finished</Label>
                  <Input
                    id="bf-to"
                    type="time"
                    value={to}
                    onChange={(e) => setTo(e.currentTarget.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {lines.map((line, i) => (
                  <div key={line.taskId} className="flex items-center gap-3">
                    <span className="flex-1 text-sm">{line.label}</span>
                    <Input
                      type="number"
                      min={0}
                      className="w-24"
                      value={line.minutes === 0 ? '' : String(line.minutes)}
                      placeholder="0"
                      aria-label={`Minutes on ${line.label}`}
                      onChange={(e) => {
                        const n = e.target.value === '' ? 0 : Number.parseInt(e.target.value, 10);
                        setLines((prev) =>
                          prev.map((l, idx) =>
                            idx === i ? { ...l, minutes: Number.isNaN(n) ? 0 : n } : l,
                          ),
                        );
                      }}
                    />
                    <Select
                      className="w-36"
                      value={line.outcome}
                      aria-label={`Outcome for ${line.label}`}
                      onChange={(e) =>
                        setLines((prev) =>
                          prev.map((l, idx) =>
                            idx === i
                              ? { ...l, outcome: e.currentTarget.value as TaskWorkOutcome }
                              : l,
                          ),
                        )
                      }
                    >
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                    </Select>
                  </div>
                ))}

                {unused.length > 0 ? (
                  <TaskPicker
                    tasks={unused}
                    placeholder="Add a task…"
                    onSelect={(taskId) => {
                      const task = openTasks.find((t) => t.id === taskId);
                      if (!task) return;
                      setLines((prev) => [
                        ...prev,
                        { taskId, label: task.title, minutes: 0, outcome: defaultOutcomeFor(task) },
                      ]);
                    }}
                  />
                ) : null}
              </div>

              <div className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] px-4 py-3 text-sm">
                <span>{remaining >= 0 ? 'Left unmapped' : 'Over the day'}</span>
                <span className="font-medium tabular-nums">
                  {Math.abs(remaining)} min of {workedMinutes}
                </span>
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submit.isPending || workedMinutes <= 0 || remaining < 0}
                >
                  {submit.isPending ? 'Saving…' : 'Save day'}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
